import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { APP_TIMEZONE } from '../config';
import { useAuth } from '../context/AuthContext';
import CondoLayout from './CondoLayout';
import { formatBytes, formatDate } from './admin/format';
import { downloadFile } from './documents/documentUtils';
import ReadableContent from './documents/ReadableContent';

const MONTH_YEAR = { month: 'short', year: 'numeric', timeZone: APP_TIMEZONE };
const monthYear = (value) => (value ? new Date(value).toLocaleDateString('en-US', MONTH_YEAR) : '');

const headingSlug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function Icon({ path, className = 'w-5 h-5' }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
    </svg>
  );
}

const ICONS = {
  download: 'M12 4v11M7 11l5 5 5-5M4 20h16',
  chevron: 'M9 6l6 6-6 6',
  back: 'M15 6l-6 6 6 6',
  down: 'M6 9l6 6 6-6',
  rules: 'M4 20h10M12 3l6 6M9 6l6 6M7.5 10.5l6-6M3 21l8-8',
  living: 'M3 11l9-8 9 8M5 10v10h14V10',
  meetings: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  finances: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  other: 'M9 12h6M9 16h6M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.6a1 1 0 0 1 .7.3l5.4 5.4a1 1 0 0 1 .3.7V19a2 2 0 0 1-2 2z'
};

function useHandbookApi() {
  const { getToken } = useAuth();
  return useMemo(() => {
    const config = (extra = {}) => ({ ...extra, headers: { Authorization: `Bearer ${getToken()}` } });
    return {
      home: (condoId) => axios.get(`${getApiBaseUrl()}/api/handbook/condo/${condoId}`, config()),
      item: (id) => axios.get(`${getApiBaseUrl()}/api/handbook/${id}`, config()),
      download: (item) => downloadFile(
        () => axios.get(`${getApiBaseUrl()}/api/handbook/${item._id}/file`, config({ responseType: 'blob' })),
        item.file.name
      ).catch(() => alert('Failed to download the file'))
    };
  }, [getToken]);
}

function Spinner() {
  return (
    <div className="text-center py-12" role="status" aria-label="Loading">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

function OwnersBadge() {
  return <span className="text-xs font-medium text-audienceOwners border border-audienceOwners/60 rounded-full px-2 whitespace-nowrap">Owners</span>;
}

const itemMeta = (item) => {
  const parts = [];
  if (item.category === 'bylaws') parts.push(`Updated ${monthYear(item.documentDate)}`);
  else if (item.category !== 'guide') parts.push(monthYear(item.documentDate));
  if (item.hasContent) parts.push(`${item.readingMinutes} min read`);
  else if (item.file) parts.push(`PDF only, ${formatBytes(item.file.size)}`);
  return parts.join(' · ');
};

function ItemRow({ item, slug, api }) {
  const body = (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 text-base font-semibold text-foreground">
        <span>{item.title}</span>
        {item.audience === 'owners' && <OwnersBadge />}
      </div>
      <p className="text-xs md:text-sm text-muted-foreground mt-0.5">{[item.description, itemMeta(item)].filter(Boolean).join(' ')}</p>
    </div>
  );
  const rowClass = 'flex-1 min-w-0 flex items-center gap-3 text-left';

  return (
    <li className="flex items-center gap-3 px-4 py-3 border-t border-border hover:bg-muted/50 transition-colors">
      {item.hasContent ? (
        <Link to={`/${slug}/handbook/${item._id}`} className={rowClass}>{body}</Link>
      ) : (
        <button type="button" onClick={() => api.download(item)} className={rowClass}>{body}</button>
      )}
      {item.file && (
        <button
          type="button"
          onClick={() => api.download(item)}
          aria-label={`Download ${item.title} PDF`}
          className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-primary border border-primary/50 rounded-md px-2.5 py-1 whitespace-nowrap hover:bg-primary/10"
        >
          <Icon path={ICONS.download} className="w-4 h-4" />PDF
        </button>
      )}
      {item.hasContent && <Icon path={ICONS.chevron} className="w-4 h-4 text-muted-foreground shrink-0" />}
    </li>
  );
}

function HandbookSection({ section, items, slug, api }) {
  const [showAll, setShowAll] = useState(false);
  const hidden = section.limit && !showAll ? Math.max(items.length - section.limit, 0) : 0;
  const shown = hidden ? items.slice(0, section.limit) : items;

  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <h2 className="flex items-center gap-2.5 px-4 py-3 text-lg font-semibold text-foreground">
        <span className="w-8 h-8 rounded-lg bg-primary/15 text-primary inline-flex items-center justify-center shrink-0">
          <Icon path={ICONS[section.key] || ICONS.other} />
        </span>
        {section.label}
      </h2>
      <ul>{shown.map((item) => <ItemRow key={item._id} item={item} slug={slug} api={api} />)}</ul>
      {hidden > 0 && (
        <button type="button" onClick={() => setShowAll(true)} className="block w-full text-left px-4 py-2.5 border-t border-border text-xs md:text-sm text-primary hover:bg-muted/50">
          All {section.label.toLowerCase()} ({items.length})
        </button>
      )}
    </section>
  );
}

function HandbookHome({ condo }) {
  const api = useHandbookApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!condo?._id) return undefined;
    let current = true;
    api.home(condo._id)
      .then((response) => { if (current) setData(response.data); })
      .catch(() => { if (current) setError('Failed to load the handbook. Please try again later.'); });
    return () => { current = false; };
  }, [api, condo]);

  if (error) return <p className="text-center py-12 text-muted-foreground">{error}</p>;
  if (!data) return <Spinner />;

  if (data.items.length === 0) {
    return (
      <div className="text-center py-12 bg-card rounded-lg border border-border">
        <p className="text-foreground text-base md:text-lg font-semibold">No handbook items yet</p>
        <p className="text-muted-foreground text-xs md:text-sm mt-2">Check back later for building information and guidelines.</p>
      </div>
    );
  }

  const sections = data.sections
    .map((section) => ({ section, items: data.items.filter((item) => section.categories.includes(item.category)) }))
    .filter(({ items }) => items.length > 0);
  // Two columns: with more than two sections, the first two stack on the left.
  const columns = sections.length > 2 ? [sections.slice(0, 2), sections.slice(2)] : sections.map((s) => [s]);

  return (
    <div>
      {data.topics.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="hidden md:inline text-sm text-muted-foreground">Popular:</span>
          {data.topics.map((topic, index) => (
            <Link
              key={topic.label}
              to={`/${condo.slug}/handbook/${topic.itemId}#${headingSlug(topic.heading)}`}
              className={`text-xs md:text-sm px-3 py-1 bg-card border border-border rounded-full text-foreground hover:bg-muted/50 ${index >= 5 ? 'hidden md:inline' : ''}`}
            >
              {topic.label}
            </Link>
          ))}
        </div>
      )}
      <div className={`grid grid-cols-1 ${columns.length > 1 ? 'md:grid-cols-2' : ''} gap-4 items-start`}>
        {columns.map((column) => (
          <div key={column[0].section.key} className="grid gap-4 min-w-0">
            {column.map(({ section, items }) => <HandbookSection key={section.key} section={section} items={items} slug={condo.slug} api={api} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Adds an id to every heading, and builds the "On this page" list from the h2s and h3s (or
// whatever the item's two biggest heading levels are).
function prepareContent(html) {
  const page = new DOMParser().parseFromString(html || '', 'text/html');
  const seen = new Map();
  const headings = [...page.querySelectorAll('h1, h2, h3, h4')].map((element) => {
    const base = headingSlug(element.textContent) || 'section';
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    element.id = count ? `${base}-${count + 1}` : base;
    return { id: element.id, text: element.textContent.trim(), level: Number(element.tagName[1]) };
  });
  const body = headings.filter((h) => h.level > 1);
  const usable = body.length ? body : headings;
  const levels = [...new Set(usable.map((h) => h.level))].sort();
  const toc = [];
  usable.forEach((h) => {
    if (h.level === levels[0]) toc.push({ ...h, children: [] });
    else if (h.level === levels[1]) {
      if (toc.length) toc[toc.length - 1].children.push(h);
      else toc.push({ ...h, children: [] });
    }
  });
  // Bylaws open with a title block in capitals before the real parts; that isn't navigation.
  const firstWithChildren = toc.findIndex((h) => h.children.length > 0);
  const titleBlock = firstWithChildren > 0 && toc.slice(0, firstWithChildren).every((h) => h.text === h.text.toUpperCase());
  const shownToc = titleBlock ? toc.slice(firstWithChildren) : toc;
  return { html: page.body.innerHTML, toc: shownToc, ids: shownToc.flatMap((h) => [h.id, ...h.children.map((child) => child.id)]) };
}

// The heading the reader is in: the last one scrolled past the top. Sections near the end
// can't scroll up that far, so at the bottom of the page the heading picked in "On this
// page" wins if it's on screen, otherwise the last heading on screen.
function useActiveHeading(ids) {
  const [active, setActive] = useState(ids[0] || null);
  useEffect(() => {
    if (ids.length === 0) return undefined;
    const update = () => {
      const tops = ids.map((id) => [id, document.getElementById(id)?.getBoundingClientRect().top]);
      let current = ids[0];
      tops.forEach(([id, top]) => { if (top !== undefined && top <= 100) current = id; });
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atBottom) {
        const onScreen = tops.filter(([, top]) => top !== undefined && top >= 0 && top < window.innerHeight).map(([id]) => id);
        const picked = decodeURIComponent(window.location.hash.slice(1));
        if (onScreen.includes(picked)) current = picked;
        else if (onScreen.length) current = onScreen[onScreen.length - 1];
      }
      setActive(current);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('hashchange', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('hashchange', update);
    };
  }, [ids]);
  return active;
}

function TocLinks({ toc, active, onPick }) {
  const isActiveGroup = (entry) => entry.id === active || entry.children.some((child) => child.id === active);
  const linkClass = (on, child) => `block px-2.5 py-1.5 rounded-md text-xs md:text-sm ${child ? 'pl-5' : ''} ${on ? 'bg-primary/10 text-primary font-medium' : child ? 'text-muted-foreground hover:text-foreground' : 'text-foreground hover:bg-muted/50'}`;
  return (
    <>
      {toc.map((entry) => (
        <div key={entry.id}>
          <a href={`#${entry.id}`} onClick={onPick} aria-current={entry.id === active ? 'location' : undefined} className={linkClass(entry.id === active, false)}>{entry.text}</a>
          {isActiveGroup(entry) && entry.children.map((child) => (
            <a key={child.id} href={`#${child.id}`} onClick={onPick} aria-current={child.id === active ? 'location' : undefined} className={linkClass(child.id === active, true)}>{child.text}</a>
          ))}
        </div>
      ))}
    </>
  );
}

function HandbookReader({ condo, itemId }) {
  const api = useHandbookApi();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    let current = true;
    setItem(null);
    setError(null);
    api.item(itemId)
      .then((response) => { if (current) setItem(response.data.item); })
      .catch((err) => { if (current) setError(err.response?.status === 404 ? 'This item isn’t available.' : 'Failed to load this item. Please try again later.'); });
    return () => { current = false; };
  }, [api, itemId]);

  const prepared = useMemo(() => prepareContent(item?.content), [item]);
  const active = useActiveHeading(prepared.ids);

  useEffect(() => {
    if (!item) return;
    const id = location.hash.slice(1);
    const target = id && document.getElementById(id);
    if (target) target.scrollIntoView({ block: 'start' });
    else window.scrollTo(0, 0);
  }, [item, location.hash]);

  const backLink = (className) => (
    <Link to={`/${condo.slug}/handbook`} className={`inline-flex items-center gap-1 text-xs md:text-sm text-primary ${className}`}>
      <Icon path={ICONS.back} className="w-4 h-4" />Handbook
    </Link>
  );
  if (error) return <div>{backLink('mb-3')}<p className="text-center py-12 text-muted-foreground">{error}</p></div>;
  if (!item) return <Spinner />;

  const hasToc = prepared.toc.length > 1;
  const activeLabel = prepared.toc.flatMap((entry) => [entry, ...entry.children]).find((entry) => entry.id === active)?.text;
  const meta = [item.categoryLabel, `${item.category === 'bylaws' ? 'Updated' : ''} ${item.category === 'guide' ? '' : formatDate(item.documentDate)}`.trim(), item.readingMinutes && `${item.readingMinutes} min read`].filter(Boolean).join(' · ');

  return (
    <div>
      {/* Phones and tablets: the way back and "On this page" stay pinned to the top while reading. */}
      <div className="lg:hidden sticky top-0 z-40 -mx-4 px-4 py-2 mb-3 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-3">
          {backLink('shrink-0 py-1.5')}
          {hasToc && (
            <button type="button" aria-expanded={tocOpen} onClick={() => setTocOpen(!tocOpen)} className="flex flex-1 min-w-0 items-center justify-between gap-2 bg-card border border-border rounded-lg px-3 py-1.5 text-xs md:text-sm text-left text-foreground">
              <span className="min-w-0 truncate">{activeLabel || 'On this page'}</span>
              <Icon path={ICONS.down} className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          )}
        </div>
        {hasToc && tocOpen && (
          <nav aria-label="On this page" className="mt-2 p-2 bg-card border border-border rounded-lg max-h-80 overflow-y-auto">
            <TocLinks toc={prepared.toc} active={active} onPick={() => setTocOpen(false)} />
          </nav>
        )}
      </div>
      {!hasToc && <div className="hidden lg:block">{backLink('mb-3')}</div>}
      <div className={`grid grid-cols-1 gap-4 items-start ${hasToc ? 'lg:grid-cols-4' : ''}`}>
        {hasToc && (
          <nav aria-label="On this page" className="hidden lg:block sticky top-0 max-h-screen overflow-y-auto bg-card border border-border rounded-lg p-2">
            <div className="px-2.5 pt-1 pb-2 mb-1 border-b border-border">{backLink('')}</div>
            <p className="px-2.5 pt-1 pb-2 text-xs md:text-sm font-semibold text-muted-foreground">On this page</p>
            <TocLinks toc={prepared.toc} active={active} />
          </nav>
        )}
        <article className={`min-w-0 ${hasToc ? 'lg:col-span-3' : ''} bg-card border border-border rounded-lg p-4 md:px-8 md:py-6`}>
          <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-4 border-b border-border">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-foreground flex flex-wrap items-center gap-2">{item.title}{item.audience === 'owners' && <OwnersBadge />}</h1>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">{meta}</p>
              {item.description && <p className="text-xs md:text-sm text-muted-foreground mt-1">{item.description}</p>}
            </div>
            {item.file && (
              <div className="sm:text-right shrink-0">
                <button type="button" onClick={() => api.download(item)} className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold text-sm rounded-lg px-3.5 py-2.5 hover:bg-primary/90">
                  <Icon path={ICONS.download} className="w-4 h-4" />Download original PDF
                </button>
                <p className="text-xs text-muted-foreground mt-1">{formatBytes(item.file.size)}{item.hasContent ? ' · the official version' : ''}</p>
              </div>
            )}
          </header>
          <div className="pt-2">
            {!item.hasContent && <p className="text-sm text-muted-foreground pt-2">There’s no text to read here. Download the PDF to read this one.</p>}
            <ReadableContent html={item.hasContent ? prepared.html : ''} images={item.images} title={item.title} />
          </div>
          <div className="mt-6 pt-4 border-t border-border">{backLink('')}</div>
        </article>
      </div>
    </div>
  );
}

function HandbookPage() {
  const { itemId } = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const legacyItem = searchParams.get('item');
  const slug = pathname.split('/').filter(Boolean)[0];

  // Old links: /:slug/handbook?item=<id>, and /:slug/documents from before the merge.
  if (pathname.endsWith('/documents')) return <Navigate to={`/${slug}/handbook`} replace />;
  if (!itemId && legacyItem) return <Navigate to={`/${slug}/handbook/${legacyItem}`} replace />;

  return (
    <CondoLayout>
      {({ condo }) => (itemId ? <HandbookReader condo={condo} itemId={itemId} /> : <HandbookHome condo={condo} />)}
    </CondoLayout>
  );
}

export default HandbookPage;
